import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { updateEmployeeManager } from '../../../../api/endpoints/corporateEmployee';
import type { CorporateEmployee } from '../../../../api/types/corporateEmployee';
import { ApiErrorAlert } from '../../../../components/ApiErrorAlert';

/** Yönetici ataması — wired to the dedicated `CorporateEmployee/UpdateManager` route, which
 *  applies server-side self / own-subtree / invalid-manager cycle guards (see
 *  api/endpoints/corporateEmployee.ts). */
export function ManagerAssignDialog({
  open,
  employee,
  candidates,
  onClose,
}: {
  open: boolean;
  employee: CorporateEmployee | null;
  candidates: CorporateEmployee[];
  onClose: () => void;
}) {
  const [managerID, setManagerID] = useState<number>(0);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (open) setManagerID(employee?.managerID ?? 0);
  }, [open, employee]);

  const mutation = useMutation({
    mutationFn: () => {
      if (!employee) return Promise.reject(new Error('Personel seçilmedi.'));
      return updateEmployeeManager({
        employeeId: employee.employeeID,
        managerId: managerID || null,
      });
    },
    onSuccess: () => {
      enqueueSnackbar('Yönetici ataması güncellendi.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['corporateEmployee', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['corporateEmployee', 'byId', employee?.employeeID] });
      onClose();
    },
    onError: () => {
      /* surfaced via ApiErrorAlert below */
    },
  });

  const options = candidates.filter((c) => c.employeeID !== employee?.employeeID);

  return (
    <Dialog open={open} onClose={() => !mutation.isPending && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle>Yönetici Ata</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {employee ? `${employee.name} ${employee.surName}` : ''} için yönetici seçin.
          </Typography>
          <TextField
            select
            fullWidth
            size="small"
            label="Yönetici"
            value={managerID}
            onChange={(e) => setManagerID(Number(e.target.value))}
          >
            <MenuItem value={0}>Yok</MenuItem>
            {options.map((c) => (
              <MenuItem key={c.employeeID} value={c.employeeID}>
                {c.name} {c.surName}
              </MenuItem>
            ))}
          </TextField>
          <ApiErrorAlert error={mutation.error} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isPending}>
          Vazgeç
        </Button>
        <Button onClick={() => mutation.mutate()} variant="contained" disabled={mutation.isPending}>
          {mutation.isPending ? 'Kaydediliyor…' : 'Kaydet'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
